import { Component, OnDestroy, ViewChild, ElementRef, AfterContentInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SiteService } from '../../services/site.service';
import { getQueryParamFromAncestorRoute } from '../../utilities/route.utilities';
import { Subscription } from 'rxjs';
import swal from 'sweetalert2';
import { BillingService } from '../../services/billing.service';
import { MasonryItemModel } from '../masonry/masonry.component';
import { AgedDebtGridItemComponent } from './grid-item/aged-debt-grid-item.component';
import { BillingPaymentsByTypeGridItemComponent } from './grid-item/billing-payments-by-type-grid-item.component';
import { CumulativeAccountBalanceGridItemComponent } from './grid-item/cumulative-account-balance-grid-item.component';
import { HistoryBillsGridItemComponent } from './grid-item/history-bills-grid-item.component';

@Component({
    selector: 'billing-portfolio',
    templateUrl: './billing-portfolio.component.html'
})

export class BillingPortfolioComponent implements AfterContentInit, OnDestroy {

    masonryItems: MasonryItemModel[] = [];

    subscriptions: Subscription[] = [];
    consumerId: string;
    userSiteId: number = 0;
    clientId: number;
    isSearching: boolean = false;
    sites: any[] = [];
    selectedSiteId: number = 0;
    agedDebt: any[] = [];
    payemntByType: any[] = [];
    accountBalance: any[] = [];
    billingHistory: any[] = [];
    billingSummary: any;
    websiteUsersPercentage: number;
    billingConsumersPercentage: number;
    billingPropertiesChange: number;
    isDecreasing: boolean = false;
    isIncreasing: boolean = false;
    billingSlaPercentage: number = 0;

    @ViewChild('timePeriod', { static: true }) timePeriod: ElementRef;
    @ViewChild('siteId', { static: true }) siteId: ElementRef;

    constructor(
        private billingService: BillingService,
        private siteService: SiteService,
        private route: ActivatedRoute,
        private router: Router) {
    }

    ngAfterContentInit(): void {
        this.subscriptions.push(this.route.queryParams.subscribe(queryParams => {
            this.onInitialise();
        }));
    }

    onInitialise() {

        this.masonryItems = [
            {
                component: AgedDebtGridItemComponent
            },
            {
                component: BillingPaymentsByTypeGridItemComponent
            },
            {
                component: CumulativeAccountBalanceGridItemComponent,
            },
            {
                component: HistoryBillsGridItemComponent,
            }
        ]

        this.subscriptions.push(this.route
            .parent!
            .params
            .subscribe(params => {
                this.clientId = +params['clientId'];
            }));
        this.userSiteId = parseInt(getQueryParamFromAncestorRoute(this.route, 'siteId'));
        if (Number.isNaN(this.userSiteId) || this.userSiteId === undefined) { this.userSiteId = 0; }

        let siteId: number = 0;
        if (this.route.snapshot.queryParams['siteId']) {
            this.selectedSiteId = this.userSiteId;
        } else {
            this.updateSite();
        }

        let timePeriod = 12;
        if (this.route.snapshot.queryParams['timePeriod']) {
            this.timePeriod.nativeElement.value =
                parseFloat(this.route.snapshot.queryParams['timePeriod']);
        } else {
            this.updateTimeperiod(timePeriod);
        }

        this.billingService.getSummary(this.clientId, this.selectedSiteId).then((result) => {
            let data = result;
            this.websiteUsersPercentage = undefined;
            this.billingConsumersPercentage = undefined;
            if (data) {
                this.billingSummary = data;

                this.billingSummary.avgPaymentLm = Math.round(this.billingSummary.avgPaymentLm);
                this.billingSummary.avgPaymentTm = Math.round(this.billingSummary.avgPaymentTm);
                this.billingSummary.cumulativeAccountBalanceLm = Math.round(this.billingSummary.cumulativeAccountBalanceLm);
                this.billingSummary.cumulativeAccountBalanceTm = Math.round(this.billingSummary.cumulativeAccountBalanceTm);

                if (this.billingSummary.websiteUsers && this.billingSummary.billingProperties > 0) this.websiteUsersPercentage = Math.round((this.billingSummary.websiteUsers / this.billingSummary.billingProperties) * 100);
                if (this.billingSummary.allProperties && this.billingSummary.billingProperties > 0) this.billingConsumersPercentage = Math.round((this.billingSummary.billingProperties / this.billingSummary.allProperties) * 100);

                this.billingPropertiesChange = this.billingSummary.billPropertiesLastMonth - this.billingSummary.billingProperties;

                this.billingSlaPercentage = (this.billingSummary.billsInSla == 0 && this.billingSummary.expecteedBills == 0) ? 0 : Math.floor((this.billingSummary.billsInSla / this.billingSummary.expecteedBills) * 100);
                if (isNaN(this.billingSlaPercentage)) { this.billingSlaPercentage = 0; }
               
                this.isIncreasing = (this.billingSummary.billingPropertiesChange && this.billingSummary.billingPropertiesChange > 0) ? true : false;
                this.isDecreasing = (this.billingSummary.billingPropertiesChange && this.billingSummary.billingPropertiesChange < 0) ? true : false;

            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    updateSite() {
        let queryParams: any = JSON.parse(JSON.stringify(this.route.snapshot.queryParams)) as any;
        if (this.selectedSiteId != undefined && this.selectedSiteId != null) queryParams.siteId = +this.selectedSiteId;
        this.router.navigate(['/portal/' + this.clientId.toString() + '/billing'], { queryParams: queryParams, replaceUrl: true });
        this.search(this.selectedSiteId);
    }

    updateTimeperiod(value: any) {
        let queryParams: any = JSON.parse(JSON.stringify(this.route.snapshot.queryParams)) as any;
        queryParams.timePeriod = +value;
        this.router.navigate(['/portal/' + this.clientId.toString() + '/billing'], { queryParams: queryParams, replaceUrl: true });
    }

    search(siteId?: number) {
        this.siteService.all(this.clientId).then((result) => {
            this.sites = result;
        });

        this.billingService.getSummary(this.clientId, siteId).then((result) => {
            let data = result;
            this.websiteUsersPercentage = undefined;
            this.billingConsumersPercentage = undefined;
            if (data) {
                this.billingSummary = data;

                this.billingSummary.avgPaymentLm = Math.round(this.billingSummary.avgPaymentLm);
                this.billingSummary.avgPaymentTm = Math.round(this.billingSummary.avgPaymentTm);
                this.billingSummary.cumulativeAccountBalanceLm = Math.round(this.billingSummary.cumulativeAccountBalanceLm);
                this.billingSummary.cumulativeAccountBalanceTm = Math.round(this.billingSummary.cumulativeAccountBalanceTm);

                if (this.billingSummary.websiteUsers && this.billingSummary.billingProperties > 0) this.websiteUsersPercentage = Math.round((this.billingSummary.websiteUsers / this.billingSummary.billingProperties) * 100);
                if (this.billingSummary.allProperties && this.billingSummary.billingProperties > 0) this.billingConsumersPercentage = Math.round((this.billingSummary.billingProperties / this.billingSummary.allProperties) * 100);

                this.billingPropertiesChange = this.billingSummary.billPropertiesLastMonth - this.billingSummary.billingProperties;

                this.isIncreasing = (this.billingSummary.billingPropertiesChange && this.billingSummary.billingPropertiesChange > 0) ? true : false;
                this.isDecreasing = (this.billingSummary.billingPropertiesChange && this.billingSummary.billingPropertiesChange < 0) ? true : false;

            }
        });

    }

    onInfoClick(widget: number) {
        if (widget === 1) {
            swal({
                title: 'Cumulative Account Balance',
                html: '<p style="text-align: left;">This is showing you the latest total of balances in each month by Credit and Debt. All accounts in credit summed and all accounts in debt summed.</p>\
              <p style="text-align: left;">This helps to expose the overall portfolio behaviour changes, and if debt or credit balances are building up.</p>',
                confirmButtonText: 'Close',
                confirmButtonColor: '#00539f'
            });
        }

        if (widget === 2) {
            swal({
                title: 'Payments By Type',
                html: '<p style="text-align: left;">This chart is showing you all payments made by consumers in your Billing portfolio.</p>\
              <p style="text-align: left;">Shown by total value of all payments in each Month & Year as per the number of full months in the page filter and current month to date, by payment method type.</p>',
                confirmButtonText: 'Close',
                confirmButtonColor: '#00539f'
            });
        }

        if (widget === 3) {
            swal({
                title: 'Aged Debt Levels',
                html: '<p style="text-align: left;">This is a stacked bar to show you the latest by month value of debt by period. </p>\
                       <p style="text-align: left;">Switch2 may be able to help manage any debt on your portfolio by utilising our Debt Management \
                        services, for more information discuss with your Client Account Manager.</p>',
                confirmButtonText: 'Close',
                confirmButtonColor: '#00539f'
            });
        }

        if (widget === 4) {
            swal({
                title: 'Billing History',
                html: '<p style="text-align: left;">This chart is showing you the total value of Bills Posted and quantity of Bills Posted by month.</p>',
                confirmButtonText: 'Close',
                confirmButtonColor: '#00539f'
            });
        }
    }

}
